import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Button,
  Checkbox,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Alert,
  Autocomplete,
  Chip,
  TextField
} from '@mui/material';
import DateInput from 'src/components/UI/input/DateInput';
import PartialDateInput from 'src/components/UI/input/PartialDateInput';
import {
  formatDate,
  toStoredDate,
  validatePhn,
  getPersonAge,
  calculateAgePartial,
  validateAge,
  validateEmail
} from 'src/util/util';
import {
  partialDateFromBackend,
  partialDateToBackend
} from 'src/util/partialDate';
import moment from 'moment';
import axios from 'src/axios';
import { announceMemberRowChanges } from 'src/store/workspace/memberRowChanges';
import { useQuery } from '@tanstack/react-query';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Visibility from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Switch from '@mui/material/Switch';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import {
  CONSTANTS,
  spouseRelations
} from 'src/components/questionnaire/Constants';
import {
  resolveEthnicityOptions,
  readEthnicitySettings
} from 'src/util/ethnicity';
import { resolveReligionOptions } from 'src/util/religion';
import ReligionInput from 'src/components/UI/input/ReligionInput';
import SelectInput from 'src/components/UI/input/SelectInput';
import BornOfPregnancyNotice from './BornOfPregnancyNotice';
import MultipleBirthStatus from './MultipleBirthStatus';
import RadioInput from 'src/components/UI/input/RadioInput';
import TextInput from 'src/components/UI/input/TextInput';
import {
  ThemeContext,
  SnackContext,
  AppContext,
  UserContext
} from 'src/store/ContextStore';
import PreviewDialogBox from '../../../smart/PreviewDialogBox';
// import {
//   getConnectedChildren,
//   getSpousesWithChildren
// } from '../PedigreeChart/utils';
import { checkRelationValid } from '../util';
import {
  isAnonymousOrLoading,
  isAnonymousActive
} from '../../anonymousPedigrees';
import { resolveVitalStatus } from '../../pedigree-utils';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import { CONSTANTS as PEDIGREE_CONSTANTS } from 'src/components/Constants';
import usePedigreeFieldLabels, {
  useSavePedigreeFieldLabel
} from 'src/hooks/usePedigreeFieldLabels';
import { FIELD_WIDTH, formGridSx, fieldSx } from '../personFieldLayout';
import { memberIdentityOf } from '../memberIdentity';
import { mergeRefreshedPersonForm } from './personFormRefresh';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const defaultValue = {
  gender: 'unknown',
  email: '',
  firstName: '',
  lastName: '',
  dob: '',
  dod: '',
  estimatedDob: false,
  estimatedDod: false,
  dobPrecision: 'full',
  dodPrecision: 'full',
  mobile: '',
  age: '',
  isAlive: 'true',
  diagnoses: [],
  diagnosesAge: {},
  spouseIds: [],
  fertilityOption: null
};

// Age shown/stored follows the FHQ principle: when a DoB (of any precision) is
// present the age is DERIVED from it (and the field is locked); only a DoB-less
// person keeps a directly-entered age. Given a record's stored dates, return
// the age to display/store — the derived age when there is a DoB, otherwise the
// record's own `age` (the anonymous / DoB-less case). DoD refines age-at-death
// only for a deceased person, matching the previous "Current age" caption.
const deriveAgeFromDates = (rec) => {
  if (!rec) return '';
  const dob = partialDateFromBackend({
    iso: rec.dob,
    estimatedFlag: rec.estimatedDob,
    precisionField: rec.dobPrecision
  });
  if (!dob) return rec.age ?? '';
  const dod =
    rec.isAlive === 'false'
      ? partialDateFromBackend({
          iso: rec.dod,
          estimatedFlag: rec.estimatedDod,
          precisionField: rec.dodPrecision
        })
      : null;
  const result = calculateAgePartial(dob, dod);
  return result ? result.age : '';
};

function PersonMenu({
  data,
  setMemberRecords,
  memberRecords,
  setError,
  setPedigreeState,
  onDirtyChange,
  saveRef,
  discardRef,
  isDisabled,
  // TGCLOUD-1669. The container opts in: the person tab does, the drawer does
  // not. Every field stays a live control either way; this only decides how
  // many sit on a row. See personFieldLayout.js.
  wideLayout = false,
  modificationHistoryHandle,
  onProbandEmailSaved,
  // Select a member on the chart by pid — used only by the born-of-a-pregnancy notice below to
  // navigate to the carrier's record. Selection lives in ChartBox, so this posts a request.
  onSelectMember
}) {
  const t = useAutoTranslation();

  const [formValues, setFormValues] = React.useState(defaultValue);
  // The fields the clinician has edited since the form was last loaded, saved
  // or discarded. What lets a refresh of the SAME person's record keep their
  // edits instead of re-seeding over them; see the [data] effect below.
  const touchedRef = React.useRef(new Set());
  // Every edit goes through here rather than straight to setFormValues, so a
  // field an edit changes as a side effect (Gender Identity sets Sex Assigned
  // at Birth, Alive clears the date of death) counts as edited too.
  const editForm = (update) =>
    setFormValues((prev) => {
      const next = update(prev);
      Object.keys(next).forEach((key) => {
        if (next[key] !== prev[key]) touchedRef.current.add(key);
      });
      return next;
    });
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResolvedFamilyId, setPreviewResolvedFamilyId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [diagnoseAgeWarnings, setDiagnoseAgeWarnings] = useState([]);
  const { theme } = useContext(ThemeContext);
  const { content } = React.useContext(AppContext);
  const pedigreeProband = content?.pedigreeSelectedUsers?.selectedUser;
  const {
    settings: {
      systemFeatures: { pedigreeLabelEditForGSA = false } = {},
      pedigreeSetting
    }
  } = content || {};
  // anonymous fails CLOSED (hides PII inputs while settings load);
  // anonymousActive fails open (anonymous-only affordances appear on resolve)
  const anonymous = isAnonymousOrLoading(pedigreeSetting);
  const anonymousActive = isAnonymousActive(pedigreeSetting);
  const { user } = useContext(UserContext);
  const { setSnack } = React.useContext(SnackContext);

  const canEditLabels =
    user?.userType === PEDIGREE_CONSTANTS.userTypes.ADMIN ||
    (user?.userType === PEDIGREE_CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN &&
      pedigreeLabelEditForGSA);

  // TGCLOUD-1669 Phase 2 — the tenant label map now comes from a shared hook
  // rather than this component's own fetch and useState. The pinned person
  // header prints the same three labels, and two independent copies would
  // visibly disagree the moment an administrator renamed a field. The query key
  // is shared, so this costs no extra request; it removes one.
  const fieldLabels = usePedigreeFieldLabels();
  const savePedigreeFieldLabel = useSavePedigreeFieldLabel();
  const [editingLabel, setEditingLabel] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState('');

  const handleLabelEdit = (field) => {
    setEditingLabel(field);
    setEditLabelValue(fieldLabels[field]);
  };

  const handleLabelSave = (field) => {
    const trimmed = editLabelValue.trim();
    if (!trimmed) return;
    // Invalidating the shared query is what makes the header above re-render
    // with the new label too, rather than only this form.
    savePedigreeFieldLabel(field, trimmed)
      .then(() => {
        setEditingLabel(null);
        setSnack({
          status: true,
          msg: t('Label updated!'),
          severity: 'success'
        });
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('Failed to update label.'),
          severity: 'error'
        });
      });
  };

  const handleLabelCancel = () => {
    setEditingLabel(null);
    setEditLabelValue('');
  };

  const [ethnicity, setEthnicity] = React.useState([]);
  const [religionSettings, setReligionSettings] = React.useState(null);
  const [DOBErrorText, setDOBErrorText] = React.useState('');
  const [DOBError, setDOBError] = React.useState(false);
  const isDOBValid = () => {
    return (
      !formValues.dob ||
      (moment(new Date()) > moment(formValues.dob) &&
        (!formValues.dod || !(moment(formValues.dod) < moment(formValues.dob))))
    );
  };

  const [DODErrorText, setDODErrorText] = React.useState('');
  const [DODError, setDODError] = React.useState(false);
  const isDODValid = () => {
    return (
      !formValues.dod ||
      (moment(new Date()) > moment(formValues.dod) &&
        (!formValues.dob || !(moment(formValues.dod) < moment(formValues.dob))))
    );
  };

  const normalize = (val) => (val ? val.toString().trim().toLowerCase() : '');

  // toStoredDate on both sides: one operand is typically a picker Date and the
  // other a stored 'YYYY-MM-DD' string, and formatDate shifts ONLY the string
  // (UTC-midnight re-parse) at negative offsets. The two then never compare
  // equal, so a genuine duplicate patient goes undetected.
  const isSameDate = (d1, d2) => {
    if (!d1 || !d2) return false;
    return toStoredDate(d1) === toStoredDate(d2);
  };

  const handlePreviewFamily = async (familyMemberId) => {
    try {
      const familyId = familyMemberId.split('-').slice(0, 3).join('-'); // G-2-00005
      const previewUrl = `/admin/pedigree/${familyId}?embed=chart&highlight=${familyMemberId}`;

      setPreviewResolvedFamilyId(familyId);
      setPreviewUrl(previewUrl);
      setPreviewOpen(true);
    } catch (err) {
      console.error('[PersonMenu] Pedigree preview failed', err);
    }
  };

  // Everyone in the tenant, for the cross-family duplicate scan.
  //
  // This used to fetch every proband unpaginated and then one getFamiliesInfo
  // request PER proband — ~5001 requests at 5000 families, and it ran on mount,
  // so opening ANY person kicked the lot off and starved everything else on the
  // backend (TGCLOUD-1749). The search-index endpoint returns the same people
  // in one read.
  //
  // It has to be React Query rather than a ref: the old cache was a useRef, so
  // every remount of this component was a fresh miss and refetched the whole
  // tenant. The key is shared with the records table and the quick switcher, so
  // whichever asks first pays for it and the rest of the session is a cache hit.
  // Duplicate detection is name/dob/phone-based, so it is skipped entirely in
  // anonymous mode.
  const { data: searchIndex } = useQuery({
    queryKey: ['familySearchIndex'],
    enabled: !anonymousActive,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const res = await axios.get(APICONSTANTS.getProbandSearchIndex);
      return res.data || [];
    }
  });

  // The index also emits a row for each family ROOT, which the old
  // per-family fetch never returned; those carry no familyMemberId, and keeping
  // them would let a family match itself twice.
  const completeCopy = React.useMemo(
    () => (searchIndex || []).filter((r) => r?.familyMemberId),
    [searchIndex]
  );

  const checkDuplicateInPedigree = (current, completeCopy) => {
    const matches = [];

    const currentFirst = normalize(current.firstName);
    const currentLast = normalize(current.lastName);
    const currentHasName = !!(currentFirst || currentLast);

    completeCopy.forEach((person) => {
      if (person.__sameFamily && person.pid === current.pid) return;
      if (normalize(person.gender) !== normalize(current.gender)) return;

      const personFirst = normalize(person.firstName);
      const personLast = normalize(person.lastName);
      const personHasName = !!(personFirst || personLast);

      const namesEqual =
        personFirst === currentFirst && personLast === currentLast;

      if (currentHasName && personHasName && !namesEqual) return;

      const nameMatch = namesEqual && currentHasName;
      const dobMatch = isSameDate(person.dob, current.dob);

      const phoneMatch =
        normalize(person.mobile) &&
        normalize(current.mobile) &&
        normalize(person.mobile) === normalize(current.mobile);

      const isDuplicate = current.dob
        ? nameMatch && dobMatch
        : nameMatch || phoneMatch;

      if (isDuplicate) {
        matches.push({
          pid: person.pid,
          upn: person.upn || person.proBandId || null,
          familyMemberId: person.familyMemberId || null,
          firstName: person.firstName,
          lastName: person.lastName,
          dob: person.dob,
          mobile: person.mobile,
          nameMatch,
          dobMatch,
          phoneMatch
        });
      }
    });

    return matches;
  };

  const renderDuplicateList = (matches) => {
    if (!matches.length) return null;

    return (
      <>
        <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
          {t('A similar members found in this family :')}
        </Typography>
        <List dense>
          {matches.map((m) => {
            const primary = m.familyMemberId && `${m.familyMemberId}`;

            const secondary = [
              `${m.firstName || ''} ${m.lastName || ''}`.trim() ||
                t('Unnamed member'),
              m.dob && t('DOB: {{dob}}', { dob: m.dob }),
              m.mobile && t('Mobile: {{mobile}}', { mobile: m.mobile })
            ]
              .filter(Boolean)
              .join(' | ');

            return (
              <ListItem
                key={m.user_id}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ListItemText
                  primary={primary}
                  secondary={secondary}
                  slotProps={{
                    primary: { fontWeight: 600 }
                  }}
                />
                {m.familyMemberId && (
                  <Tooltip title={t('Preview pedigree')}>
                    <IconButton
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewFamily(m.familyMemberId);
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
            );
          })}
        </List>
      </>
    );
  };

  const getSpouseRelInfo = useCallback(
    (spouseId) => {
      const rel = data?.spouseRelationships?.[spouseId] || {};
      return {
        parentRelationship: rel.parentRelationship ?? spouseRelations.MARRIED,
        isConsanguineous: rel.isConsanguineous ?? false,
        fertilityOption: rel.fertility ?? null
      };
    },
    [data]
  );

  const handleChange = (e) => {
    onDirtyChange?.(true);

    const value =
      typeof e.target.value === 'string'
        ? e.target.value.trim()
        : e.target.value;

    if (e.target.name === 'genderIdentity') {
      const relationValid = checkRelationValid(
        CONSTANTS.genderMappings[value],
        memberRecords,
        formValues
      );

      if (!relationValid.status) {
        setError(t(relationValid.message));
        return;
      }

      editForm((prevState) => ({
        ...prevState,
        [e.target.name]: value,
        gender: CONSTANTS.genderMappings[value]
      }));
    }
    if (e.target.type === 'checkbox') {
      editForm((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.checked
      }));
    } else {
      const additionalFormValues = {};
      if (e.target.name === 'selectedSpouse') {
        const { parentRelationship, isConsanguineous, fertilityOption } =
          getSpouseRelInfo(e.target.value);

        additionalFormValues.spouseRelationship = parentRelationship;
        additionalFormValues.isConsanguineous = isConsanguineous;
        additionalFormValues.fertilityOption = fertilityOption;
      }

      // Convert empty string back to null for fertilityOption
      const resolvedValue =
        e.target.name === 'fertilityOption' && value === '' ? null : value;

      editForm((prevState) => ({
        ...prevState,
        [e.target.name]: resolvedValue,
        ...(e.target.name === 'isAlive' && resolvedValue === 'true'
          ? { dod: '' }
          : {}),
        ...additionalFormValues
      }));
    }
  };

  const dateChangeHandler = (type, value) => {
    onDirtyChange?.(true);

    let formattedDate = formatDate(value);
    let invalidDate = formattedDate.includes('NaN');

    editForm((prevState) => ({
      ...prevState,
      [type]: value,
      ...(type === 'dod' && !invalidDate ? { isAlive: 'false' } : {})
    }));
  };

  const getFormValuesFromData = useCallback(() => {
    if (!data) return defaultValue;
    const formValuesData = {
      pid: data.pid,
      spouse: data.spouse ?? null,
      gender: data.gender ? data.gender : 'unknown',
      genderIdentity: data.genderIdentity,
      email: data.email ?? '',
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      dob: data.dob ?? null,
      dod: data.dod ?? null,
      estimatedDod: data['estimatedDod'],
      estimatedDob: data['estimatedDob'],
      dobPrecision: data.dobPrecision ?? (data.estimatedDob ? 'year' : 'full'),
      dodPrecision: data.dodPrecision ?? (data.estimatedDod ? 'year' : 'full'),
      mobile: data.mobile ?? '',
      age: data.age ?? '',
      // Keep the STORED encoding here ('' = no assertion on record). TGCLOUD-1582
      // wants the radio to read Alive by default, but that is a display concern —
      // resolveVitalStatus is applied at the RadioGroup below. Baking 'true' into
      // formValues instead would (a) disable the Date-of-Death picker for every
      // member whose status was never recorded, since it disables on
      // isAlive === 'true', breaking the "type a DoD → auto-mark Deceased" path,
      // and (b) make any unrelated edit persist an affirmative alive assertion.
      isAlive: data.isAlive ?? (data.dod ? 'false' : ''),
      diagnoses: data.diagnoses ?? [],
      diagnosesAge: data.diagnosesAge ?? {},
      isHide: data.isHide ?? false,
      recordsReviewed: data.recordsReviewed ?? false,
      ethnicity: data.ethnicity ?? [],
      religion: data.religion ?? null,
      hospitalNumber: data.hospitalNumber ?? '',
      healthcareId: data.healthcareId ?? '',
      externalPatientIds: data.externalPatientIds ?? [],
      spouseIds: data.spouseIds ?? []
    };
    if (data.spouseIds?.length > 0) {
      const selectedSpouse = data.spouseIds[0];
      const { parentRelationship, isConsanguineous, fertilityOption } =
        getSpouseRelInfo(selectedSpouse);
      formValuesData.selectedSpouse = selectedSpouse;
      formValuesData.spouseRelationship = parentRelationship;
      formValuesData.isConsanguineous = isConsanguineous;
      formValuesData.fertilityOption = fertilityOption;
    } else {
      formValuesData.fertilityOption = data.fertility ?? null;
    }
    return formValuesData;
  }, [data, getSpouseRelInfo]);

  // A NEW `data` IS NOT ALWAYS A NEW PERSON.
  //
  // This used to re-seed the form and mark it clean on every new `data`. For a
  // change of person that is right, and the selection guard now asks before
  // one discards anything. But ChartBox also replaces `data` for the SAME
  // person whenever their record refreshes: a structural sync after a relative
  // is added to them, a spouse link, a date of birth changed elsewhere. The
  // re-seed then discarded the clinician's unsaved edits without a word.
  //
  // Same person (memberIdentityOf: `_id`, never the pid) with edits pending:
  // merge. The fields the clinician touched keep their values, every other
  // field takes the refreshed one, and the form stays dirty while any touched
  // field still differs from the record. A different person, or a clean form,
  // re-seeds exactly as before.
  const loadedIdentityRef = React.useRef(undefined);
  React.useEffect(() => {
    const identity = memberIdentityOf(data);
    const samePerson =
      identity != null && loadedIdentityRef.current === identity;
    loadedIdentityRef.current = identity;
    const refreshed = getFormValuesFromData();

    if (samePerson && touchedRef.current.size > 0) {
      const { values, kept, dirty } = mergeRefreshedPersonForm(
        formValues,
        refreshed,
        touchedRef.current
      );
      setFormValues(values);
      if (dirty) {
        touchedRef.current = new Set(
          [...touchedRef.current].filter((key) => kept.has(key))
        );
        return;
      }
    }

    touchedRef.current = new Set();
    setFormValues(refreshed);
    onDirtyChange?.(false);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    // toStoredDate, NOT formatDate: formValues is seeded from the stored member
    // record, so these are already 'YYYY-MM-DD' strings. formatDate re-parses a
    // bare date as UTC midnight and hands back the previous day at negative
    // offsets, so every save walked the date backwards by one.
    if (formValues.dob) formValues.dob = toStoredDate(formValues.dob);
    if (formValues.dod) formValues.dod = toStoredDate(formValues.dod);

    // Find the target node to update
    const node = memberRecords.filter((p) => p.pid === data.pid)[0];

    if (!node) {
      setSnack({
        status: true,
        msg: t('Could not save: this person has no underlying record.'),
        severity: 'error'
      });
      return;
    }

    /* eslint-disable no-unused-vars */
    const {
      diagnoses: _d,
      diagnosesAge: _da,
      spouseIds: _s,
      selectedSpouse,
      spouseRelationship,
      isConsanguineous,
      fertilityOption,
      ...formValuesForSave
    } = formValues;
    /* eslint-enable no-unused-vars */

    if (selectedSpouse && spouseRelationship !== undefined) {
      formValuesForSave.spouseRelationships = {
        ...(node.spouseRelationships || {}),
        [selectedSpouse]: {
          parentRelationship: spouseRelationship,
          isConsanguineous: isConsanguineous ?? false,
          fertility: fertilityOption ?? null
        }
      };
      formValuesForSave.fertility = fertilityOption ?? null;
    } else {
      formValuesForSave.fertility = fertilityOption ?? null;
    }

    // Age persistence. A directly-entered age is stored ONLY for a DoB-less
    // person (the anonymous / age-known-but-not-DoB case). When a DoB is
    // present it is the single source of truth — age derives live everywhere
    // (getPersonAge, the on-canvas age label) — so we never persist a derived
    // snapshot that would silently go stale (the CanRisk-age trap). number when
    // set, null to clear a stored value, key removed when never set so
    // unaffected payloads are byte-identical.
    if (formValuesForSave.dob) {
      if (data?.age == null) delete formValuesForSave.age;
      else formValuesForSave.age = null;
    } else if (formValuesForSave.age === '' || formValuesForSave.age == null) {
      if (data?.age == null) delete formValuesForSave.age;
      else formValuesForSave.age = null;
    } else {
      formValuesForSave.age = Number(formValuesForSave.age);
    }

    const updatedNode = { ...node, ...formValuesForSave };

    const newDataset = memberRecords.map((person) => {
      if (person.pid === data.pid) return { ...person, ...updatedNode };

      if (selectedSpouse && person.pid === selectedSpouse) {
        return {
          ...person,
          fertility: fertilityOption ?? null,
          spouseRelationships: {
            ...(person.spouseRelationships || {}),
            [data.pid]: {
              parentRelationship: spouseRelationship,
              isConsanguineous: isConsanguineous ?? false,
              fertility: fertilityOption ?? null
            }
          }
        };
      }

      return person;
    });

    // handleDetailsChange([updatedNode]);

    setMemberRecords(newDataset);

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onDirtyChange?.(false);
    touchedRef.current = new Set();

    // PROGRESS, NOT COMPLETION. Nothing has been written to the server at this
    // point: the edit has been put into `memberRecords`, and the only code that
    // POSTs those is PedigreeChart's debounced autosave, which has not run yet
    // and may be refused (a colleague holding the family lock, the 409
    // foreign-member gate, a 413, a dropped connection). This used to say
    // "Saved", so a refused write was preceded by a green tick — the exact
    // defect the SAVE_NOT_SENT work exists to remove.
    //
    // The one completion message comes from the autosave's settle handler: the
    // "Saved just now" chip on success, the failure snackbar otherwise. No
    // second success snack here.
    setSnack({
      status: true,
      msg: t('Saving…'),
      severity: 'info'
    });

    const gNum = data?.proBandId ?? data?.familyMemberId;
    if (gNum) {
      const synced = {
        firstName: formValuesForSave.firstName || null,
        lastName: formValuesForSave.lastName || null,
        dob: formValuesForSave.dob || null,
        gender: formValuesForSave.gender || null
      };
      try {
        const res = await axios.post(
          `${APICONSTANTS.syncPersonLinkSecondaries}/${encodeURIComponent(
            gNum
          )}`,
          synced
        );
        // The sync wrote these values onto the person's linked records in
        // OTHER families. Any of those families open in another tab holds the
        // old values, and its next autosave would write them back over the
        // sync. Every mounted chart hears this, and one holding such a row
        // refreshes it before it may save (useExternalMemberRowRefresh).
        announceMemberRowChanges({
          memberIds: res?.data?.secondaries || [],
          fields: Object.keys(synced),
          patch: synced,
          source: 'person-link-sync'
        });
      } catch {
        /* ignore sync failures */
      }
    }

    const probandRootId = pedigreeProband?._id;
    const isEditingProband = !!data && (!!data.isProband || !!data.proBandId);

    if (
      isEditingProband &&
      probandRootId &&
      !data?.email &&
      formValues.email &&
      validateEmail(formValues.email)
    ) {
      try {
        await axios.put(
          APICONSTANTS.setProbandEmail.replace(':user_id', probandRootId),
          { email: formValues.email }
        );
      } catch (err) {
        if (err?.response?.status !== 409) {
          setSnack({
            status: true,
            msg:
              err?.response?.data?.error ||
              t('Person details saved, but the email could not be saved.'),
            severity: 'warning'
          });
        }
      } finally {
        onProbandEmailSaved?.();
      }
    }
  };

  const displayedEthnicities = React.useMemo(
    () => resolveEthnicityOptions(ethnicity),
    [ethnicity]
  );

  const displayedReligions = React.useMemo(
    () => resolveReligionOptions(religionSettings),
    [religionSettings]
  );

  const fetchEthnicity = async () => {
    try {
      const res = await axios.get(APICONSTANTS.getEthnicity);
      setEthnicity(readEthnicitySettings(res.data));
    } catch (err) {
      console.log('Error fetching ethnicity:', err);
    }
  };

  const fetchReligion = async () => {
    try {
      const res = await axios.get(APICONSTANTS.getReligion);
      setReligionSettings(res.data);
    } catch (err) {
      console.log('Error fetching religion:', err);
    }
  };

  React.useEffect(() => {
    fetchEthnicity();
    fetchReligion();

    let err = !isDOBValid();
    setDOBError(err);

    err = !isDODValid();
    setDODError(err);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validDates = () => {
    return isDOBValid() && isDODValid();
  };

  const getDiagnoseAgeWarnings = useCallback((dob, diagnoses) => {
    if (!dob || !diagnoses || diagnoses.length === 0) return [];

    const personAge = getPersonAge({ dob });
    if (personAge === null || personAge === undefined) return [];

    const warnings = [];
    diagnoses.forEach((d) => {
      const diagAge = Number(d.age);
      if (Number.isFinite(diagAge) && diagAge > personAge) {
        warnings.push({
          name: d.name,
          diagnosisAge: diagAge,
          personAge
        });
      }
    });
    return warnings;
  }, []);

  useEffect(() => {
    const warnings = getDiagnoseAgeWarnings(formValues.dob, data?.diagnoses);
    setDiagnoseAgeWarnings(warnings);
  }, [formValues.dob, data?.diagnoses, getDiagnoseAgeWarnings]);

  const onSave = async () => {
    if (!validDates()) {
      const element = document.getElementById('top');
      element.scrollIntoView();
      setDODErrorText(t('DOD cannot be in future or before DOB'));
      setDODError(true);
      if (!isDOBValid()) {
        setDOBErrorText(t('DOB cannot be in future or after DOD'));
        setDOBError(true);
      }
      setSnack({
        status: true,
        msg: t('Dates were not valid'),
        severity: 'warning'
      });
      return;
    }

    const latestDiagnoses =
      memberRecords?.find((r) => r.pid === data.pid)?.diagnoses ??
      data?.diagnoses;
    // dob is hidden in anonymous mode, so a dob-derived block would be unfixable
    const currentWarnings = anonymousActive
      ? []
      : getDiagnoseAgeWarnings(formValues.dob, latestDiagnoses);
    if (currentWarnings.length > 0) {
      const element = document.getElementById('top');
      element.scrollIntoView();
      setSnack({
        status: true,
        msg: t(
          "One or more diagnoses have an age of diagnosis greater than the person's age."
        ),
        severity: 'warning'
      });
      return;
    }

    const currentFamilyId = (data?.proBandId ?? data?.familyMemberId ?? '')
      .split('-')
      .slice(0, 3)
      .join('-');

    const scanSet = [
      ...(memberRecords || []).map((m) => ({ ...m, __sameFamily: true })),
      ...completeCopy
        .filter((p) => !currentFamilyId || p.familyId !== currentFamilyId)
        .map((m) => ({ ...m, __sameFamily: false }))
    ];

    const duplicates = anonymousActive
      ? []
      : checkDuplicateInPedigree(formValues, scanSet);

    if (duplicates.length > 0) {
      const formattedDuplicates = duplicates.map((d) => ({
        user_id: d.pid,
        firstName: d.firstName,
        lastName: d.lastName,
        familyMemberId: d.familyMemberId,
        // Stored records, so dob is already 'YYYY-MM-DD' — formatDate would
        // re-parse and show the duplicate's date a day early.
        dob: d.dob ? toStoredDate(d.dob) : null,
        mobile: d.mobile,
        nameMatch: d.nameMatch,
        dobMatch: d.dobMatch,
        phoneMatch: d.phoneMatch
      }));

      setDuplicateMatches(formattedDuplicates);
      setDuplicateDialogOpen(true);
      return;
    }
    await handleSubmit();
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef)
      discardRef.current = () => {
        touchedRef.current = new Set();
        setFormValues(getFormValuesFromData());
        onDirtyChange?.(false);
      };
  });

  return (
    // The 600px cap and the centring stay for the DRAWER, where they are the
    // signed-off behaviour and where 600px is wider than the column anyway. In
    // the person tab they are exactly the dead space this change is for, so the
    // form takes the width it is given (TGCLOUD-1669).
    <Box
      sx={
        wideLayout
          ? { display: 'flex' }
          : { display: 'flex', justifyContent: 'center', alignItems: 'center' }
      }
    >
      <div
        style={wideLayout ? { width: '100%', minWidth: 0 } : { maxWidth: 600 }}
        className="user-form"
      >
        {/* This person was created FROM someone else's pregnancy record. It belongs here rather than
            in the Reproductive section, which now lists only the individual's OWN pregnancies —
            TGCLOUD-1617 item 5. Renders nothing when the person was not born of a record. */}
        <BornOfPregnancyNotice
          member={data}
          memberRecords={memberRecords}
          onSelectMember={onSelectMember}
        />
        <Box sx={formGridSx(wideLayout, 2)}>
          {/* gender */}
          <Box id="top" sx={fieldSx(FIELD_WIDTH.wide)}>
            <FormControl component="fieldset">
              <Grid container spacing={1}>
                <Grid>
                  <FormLabel>Gender </FormLabel>
                </Grid>
                <Grid size={12}>
                  <SelectInput
                    fullWidth={true}
                    variant="outlined"
                    label={formValues.genderIdentity}
                    name="genderIdentity"
                    options={CONSTANTS.genderIdentityOptions}
                    // Only a real identity, or a definite male/female gender,
                    // fills this. An UNKNOWN/unselected node resolves to '' so
                    // the native select shows its "Select" placeholder rather
                    // than falling through to its first enabled option ('Male')
                    // — which mis-represented an unknown node as male AND made
                    // picking Male a no-op change (the displayed value already
                    // matched, so onChange never fired). The capitalised
                    // fall-through only ever produced a valid option for
                    // male/female; 'unknown' → 'Unknown' matched nothing.
                    value={
                      formValues.genderIdentity ||
                      (formValues.gender === 'male'
                        ? 'Male'
                        : formValues.gender === 'female'
                        ? 'Female'
                        : '')
                    }
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Box>

          <Box sx={fieldSx()}>
            <div className="margin-top-3"></div>
            <RadioInput
              label="Sex Assigned at Birth"
              name="gender"
              value={formValues.gender || ''}
              options={CONSTANTS.genderOptions}
              row
              disabled
            />
          </Box>

          {/* isAlive */}
          <Box sx={fieldSx()}>
            <div className="margin-top-3"></div>
            <FormControl component="fieldset">
              <FormLabel component="label" className="form-label">
                Alive or Deceased?
              </FormLabel>
              <RadioGroup
                aria-label="isAlive"
                name="isAlive"
                // TGCLOUD-1582: an unrecorded vital status reads as Alive here
                // (and normalises the legacy 'Yes'/'No'/0/boolean encodings) via
                // the same helper the bulk-edit grid uses, so panel and grid
                // agree. Display only — see the isAlive note in the form init.
                value={resolveVitalStatus(formValues)}
                onChange={handleChange}
                row
              >
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" disabled={isDisabled} />}
                  label={t('Alive')}
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" disabled={isDisabled} />}
                  label={t('Deceased')}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* name */}
          {!anonymous && (
            <Box sx={fieldSx(FIELD_WIDTH.wide)}>
              <Box sx={formGridSx(wideLayout, 1)}>
                <Box sx={fieldSx()}>
                  <FormLabel component="label" className="form-label">
                    First Name
                  </FormLabel>
                  <TextInput
                    name="firstName"
                    value={formValues.firstName}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Box>
                <Box sx={fieldSx()}>
                  <FormLabel component="label" className="form-label">
                    Last Name
                  </FormLabel>
                  <TextInput
                    name="lastName"
                    value={formValues.lastName}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* date */}
          {!anonymous && (
            <Box sx={fieldSx(FIELD_WIDTH.wide)}>
              <Box sx={formGridSx(wideLayout, 1)}>
                {diagnoseAgeWarnings.length > 0 && (
                  <Box sx={fieldSx(FIELD_WIDTH.wide)}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {diagnoseAgeWarnings.map((w, idx) => (
                        <div key={idx}>
                          <strong>{w.name}</strong> {t('has age of diagnosis')}{' '}
                          <strong>{w.diagnosisAge}</strong>{' '}
                          {t('which is more than the assigned age')}{' '}
                          <strong>{w.personAge}</strong>
                          {t(
                            ". Age of diagnosis must be less than or equal to the person's age."
                          )}
                        </div>
                      ))}
                    </Alert>
                  </Box>
                )}
                <Box sx={fieldSx()}>
                  <PartialDateInput
                    key={`dob-${data?.pid ?? 'new'}`}
                    label="Date of Birth"
                    value={partialDateFromBackend({
                      iso: formValues.dob,
                      estimatedFlag: formValues.estimatedDob,
                      precisionField: formValues.dobPrecision
                    })}
                    onChange={(partial) => {
                      setDOBError(false);
                      setDODError(false);
                      const patch = partialDateToBackend(partial, {
                        date: 'dob',
                        estimated: 'estimatedDob',
                        precision: 'dobPrecision'
                      });
                      editForm((prev) => ({ ...prev, ...patch }));
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled}
                    disableFuture
                  />
                </Box>
                <Box sx={fieldSx()}>
                  <PartialDateInput
                    key={`dod-${data?.pid ?? 'new'}`}
                    label="Date of Death"
                    value={partialDateFromBackend({
                      iso: formValues.dod,
                      estimatedFlag: formValues.estimatedDod,
                      precisionField: formValues.dodPrecision
                    })}
                    onChange={(partial) => {
                      setDOBError(false);
                      setDODError(false);
                      const patch = partialDateToBackend(partial, {
                        date: 'dod',
                        estimated: 'estimatedDod',
                        precision: 'dodPrecision'
                      });
                      editForm((prev) => ({ ...prev, ...patch }));
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled || formValues.isAlive === 'true'}
                    disableFuture
                  />
                </Box>
                {/* Age — enter directly when there is no DoB, else derived from
                    the DoB (any precision) and locked (FHQ principle). Lets a
                    clinician record a known age without computing a birth year
                    by hand. The derived value is DISPLAY-only: DoB stays the
                    single source of truth, so it is never persisted alongside a
                    DoB (see handleSubmit) — getPersonAge / labels derive live. */}
                <Box sx={fieldSx()}>
                  <FormLabel component="label" className="form-label">
                    Age
                  </FormLabel>
                  <TextInput
                    name="age"
                    type="number"
                    value={
                      formValues.dob
                        ? deriveAgeFromDates(formValues)
                        : formValues.age ?? ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || validateAge(raw)) {
                        editForm((prev) => ({ ...prev, age: raw }));
                        onDirtyChange?.(true);
                      }
                    }}
                    inputProps={{ min: 0, max: 130, step: 1 }}
                    disabled={isDisabled || !!formValues.dob}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* approximate age — anonymous mode only (no dob is captured) */}
          {anonymousActive && (
            <Box sx={fieldSx()}>
              <FormLabel component="label" className="form-label">
                Approximate Age
              </FormLabel>
              <TextInput
                name="age"
                type="number"
                value={formValues.age ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '' || (/^\d+$/.test(raw) && Number(raw) <= 130))
                    handleChange(e);
                }}
                inputProps={{ min: 0, max: 130, step: 1 }}
                disabled={isDisabled}
              />
            </Box>
          )}

          {/* ethnicity */}

          <Box sx={fieldSx(FIELD_WIDTH.wide)}>
            <FormControl component="fieldset" fullWidth>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    Ethnicity
                  </FormLabel>
                  <AutocompleteInput
                    label={t('Ethnicity')}
                    multiple
                    options={displayedEthnicities}
                    getOptionLabel={(option) => option}
                    value={formValues?.ethnicity || []}
                    onChange={(val) =>
                      handleChange({
                        target: { name: 'ethnicity', value: val }
                      })
                    }
                    disabled={isDisabled}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Box>

          <Box sx={fieldSx(FIELD_WIDTH.wide)}>
            <FormControl component="fieldset" fullWidth>
              <Grid container spacing={1}>
                <Grid size={12}>
                  <FormLabel component="label" className="form-label">
                    Religion
                  </FormLabel>
                  <ReligionInput
                    options={displayedReligions}
                    value={formValues?.religion || null}
                    onChange={(val) =>
                      handleChange({
                        target: { name: 'religion', value: val }
                      })
                    }
                    disabled={isDisabled}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Box>

          {/* contact */}
          {!anonymous && (
            <Box sx={fieldSx(FIELD_WIDTH.wide)}>
              <div className="margin-top-3"></div>
              <Box sx={formGridSx(wideLayout, 2)}>
                <Box sx={fieldSx()}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {editingLabel === 'mobile' ? (
                      <>
                        <input
                          autoFocus
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleLabelSave('mobile');
                            if (e.key === 'Escape') handleLabelCancel();
                          }}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 6px',
                            width: 120
                          }}
                        />
                        <Tooltip title={t('Save')}>
                          <IconButton
                            size="small"
                            onClick={() => handleLabelSave('mobile')}
                          >
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Cancel')}>
                          <IconButton size="small" onClick={handleLabelCancel}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <FormLabel component="label" className="form-label">
                          {fieldLabels.mobile}
                        </FormLabel>
                        {canEditLabels && (
                          <Tooltip title={t('Edit label')}>
                            <IconButton
                              size="small"
                              onClick={() => handleLabelEdit('mobile')}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                  <TextInput
                    name="mobile"
                    value={formValues.mobile}
                    onChange={(e) => {
                      if (validatePhn(e.target.value) || e.target.value === '')
                        handleChange(e);
                    }}
                    disabled={isDisabled}
                  />
                </Box>
                <Box sx={fieldSx()}>
                  <TextInput
                    label="Email"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    updateParentOnBlur
                    // Typed but not yet blurred, so not in formValues: still
                    // the clinician's edit if the record refreshes meanwhile.
                    onDirty={() => {
                      touchedRef.current.add('email');
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled}
                  />
                </Box>

                {/* Hospital Number */}
                <Box sx={fieldSx()}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {editingLabel === 'hospitalNumber' ? (
                      <>
                        <input
                          autoFocus
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              handleLabelSave('hospitalNumber');
                            if (e.key === 'Escape') handleLabelCancel();
                          }}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 6px',
                            width: 140
                          }}
                        />
                        <Tooltip title={t('Save')}>
                          <IconButton
                            size="small"
                            onClick={() => handleLabelSave('hospitalNumber')}
                          >
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Cancel')}>
                          <IconButton size="small" onClick={handleLabelCancel}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <FormLabel component="label" className="form-label">
                          {fieldLabels.hospitalNumber}
                        </FormLabel>
                        {canEditLabels && (
                          <Tooltip title={t('Edit label')}>
                            <IconButton
                              size="small"
                              onClick={() => handleLabelEdit('hospitalNumber')}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                  <TextInput
                    name="hospitalNumber"
                    value={formValues.hospitalNumber || ''}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Box>

                {/* Healthcare ID */}
                <Box sx={fieldSx()}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {editingLabel === 'healthcareId' ? (
                      <>
                        <input
                          autoFocus
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              handleLabelSave('healthcareId');
                            if (e.key === 'Escape') handleLabelCancel();
                          }}
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 6px',
                            width: 120
                          }}
                        />
                        <Tooltip title={t('Save')}>
                          <IconButton
                            size="small"
                            onClick={() => handleLabelSave('healthcareId')}
                          >
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('Cancel')}>
                          <IconButton size="small" onClick={handleLabelCancel}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <FormLabel component="label" className="form-label">
                          {fieldLabels.healthcareId}
                        </FormLabel>
                        {canEditLabels && (
                          <Tooltip title={t('Edit label')}>
                            <IconButton
                              size="small"
                              onClick={() => handleLabelEdit('healthcareId')}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                  <TextInput
                    name="healthcareId"
                    value={formValues.healthcareId || ''}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                </Box>
                <Box sx={fieldSx(FIELD_WIDTH.wide)}>
                  <FormLabel component="label" className="form-label">
                    External Patient IDs
                  </FormLabel>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formValues.externalPatientIds || []}
                    onChange={(_, newValue) => {
                      editForm((prev) => ({
                        ...prev,
                        externalPatientIds: newValue
                      }));
                      onDirtyChange?.(true);
                    }}
                    disabled={isDisabled}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          size="small"
                          {...getItemProps({ index })}
                          sx={{
                            backgroundColor: 'rgba(43, 169, 191, 0.15)',
                            color: '#1a7a8a',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: '10px',
                            '& .MuiChip-deleteIcon': {
                              color: '#1a7a8a',
                              '&:hover': { color: '#0f5a67' }
                            }
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('Type an ID and press Enter')}
                        size="small"
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {formValues.spouseIds?.length > 0 && (
            <Box sx={fieldSx(FIELD_WIDTH.wide)}>
              <Box sx={formGridSx(wideLayout, 3)}>
                {/* Spouse Relationship */}
                <Box sx={fieldSx()}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      alignItems: 'flex-end'
                    }}
                  >
                    {formValues.spouseIds.length > 1 && (
                      <Grid size={12}>
                        <FormControl component="fieldset" fullWidth>
                          <Grid container spacing={1}>
                            <Grid>
                              <FormLabel>Select Spouse</FormLabel>
                            </Grid>
                            <Grid size={12}>
                              <SelectInput
                                fullWidth={true}
                                variant="outlined"
                                label={t('Select Spouse')}
                                name="selectedSpouse"
                                translateOptions={false}
                                options={formValues.spouseIds.map((id) => {
                                  const s = memberRecords.find(
                                    (p) => p.pid === id
                                  );
                                  const label = s
                                    ? `${s.firstName || ''} ${
                                        s.lastName || ''
                                      } (UPN ${s.upn})`.trim() || `UPN ${s.upn}`
                                    : `UPN ${id}`;
                                  return { label, value: id };
                                })}
                                value={formValues.selectedSpouse}
                                onChange={handleChange}
                                disabled={isDisabled}
                              />
                            </Grid>
                          </Grid>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid size={12}>
                      <FormControl component="fieldset" fullWidth>
                        <Grid container spacing={1}>
                          <Grid>
                            <FormLabel mLabel>Spouse Relationship</FormLabel>
                          </Grid>
                          <Grid size={12}>
                            <SelectInput
                              fullWidth={true}
                              variant="outlined"
                              label={t('Spouse Relationship')}
                              name="spouseRelationship"
                              options={CONSTANTS.spouseRelationshipOptions}
                              value={formValues.spouseRelationship}
                              onChange={handleChange}
                              disabled={isDisabled}
                            />
                          </Grid>
                        </Grid>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
                {/* Fertility */}
                <Box sx={fieldSx()}>
                  <FormControl component="fieldset" fullWidth>
                    <Grid container spacing={1}>
                      <Grid>
                        <FormLabel>Fertility</FormLabel>
                      </Grid>
                      <Grid size={12}>
                        <SelectInput
                          fullWidth={true}
                          variant="outlined"
                          label={t('Fertility')}
                          name="fertilityOption"
                          options={PEDIGREE_CONSTANTS.fertilityMap?.map(
                            (o) => ({
                              label: o.label,
                              value: o.value === null ? '' : o.value
                            })
                          )}
                          value={formValues.fertilityOption ?? ''}
                          onChange={handleChange}
                          disabled={isDisabled}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Box>
                {/* Consanguinity status */}
                <Box sx={fieldSx()}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      alignItems: 'center'
                    }}
                  >
                    <Grid size={5}>
                      <FormLabel component="label" className="form-label">
                        Consanguineous
                      </FormLabel>
                    </Grid>
                    <Grid
                      container
                      size={7}
                      sx={{
                        justifyContent: 'flex-end'
                      }}
                    >
                      <Switch
                        checked={!!formValues.isConsanguineous}
                        onChange={handleChange}
                        name="isConsanguineous"
                        disabled={isDisabled}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}
          {/* Fertility  */}
          {(!formValues.spouseIds || formValues.spouseIds.length === 0) && (
            <Box sx={fieldSx()}>
              <FormControl component="fieldset" fullWidth>
                <Grid container spacing={1}>
                  <Grid>
                    <FormLabel>Fertility</FormLabel>
                  </Grid>
                  <Grid size={12}>
                    <SelectInput
                      fullWidth={true}
                      variant="outlined"
                      label={t('Fertility')}
                      name="fertilityOption"
                      options={PEDIGREE_CONSTANTS.fertilityMap?.map((o) => ({
                        label: o.label,
                        value: o.value === null ? '' : o.value
                      }))}
                      value={formValues.fertilityOption ?? ''}
                      onChange={handleChange}
                      disabled={isDisabled}
                    />
                  </Grid>
                </Grid>
              </FormControl>
            </Box>
          )}
          {/* Twin status, and the only way to correct it (TGCLOUD-1643). Twinship
              was chart-only before this — readable from the diagonals and nowhere
              else — so a sibship recorded wrongly by an FHQ could not be spotted,
              let alone fixed.

              Sits AFTER Fertility, which is where the two fertility branches (with
              spouse / without) have both closed. It belongs with the reproductive
              attributes rather than above Gender, where the first pass put it and
              where it read as a header for the whole form. Renders nothing for a
              person with no full siblings and no twin group, so it drops in
              unconditionally. */}
          <Box sx={fieldSx(FIELD_WIDTH.wide)}>
            <MultipleBirthStatus
              member={data}
              memberRecords={memberRecords}
              setMemberRecords={setMemberRecords}
              setPedigreeState={setPedigreeState}
              isDisabled={isDisabled}
              onSelectMember={onSelectMember}
            />
          </Box>
          {/* Hide / Unhide */}
          <Box sx={fieldSx()}>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid size={6}>
                <FormLabel component="label" className="form-label">
                  Hide on Pedigree
                </FormLabel>
              </Grid>
              <Grid
                container
                size={6}
                sx={{
                  justifyContent: 'flex-end'
                }}
              >
                <Switch
                  checked={!!formValues.isHide}
                  onChange={handleChange}
                  name="isHide"
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={fieldSx()}>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center'
              }}
            >
              <Grid size={6}>
                <FormLabel component="label" className="form-label">
                  Records Reviewed
                </FormLabel>
              </Grid>
              <Grid
                container
                size={6}
                sx={{
                  justifyContent: 'flex-end'
                }}
              >
                <Switch
                  checked={!!formValues.recordsReviewed}
                  onChange={handleChange}
                  name="recordsReviewed"
                  disabled={isDisabled}
                />
              </Grid>
            </Grid>
          </Box>
          {/* change history (admin/clinician sidebar only) */}
          {modificationHistoryHandle && (
            <Box
              sx={{
                ...fieldSx(FIELD_WIDTH.wide),
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <IconButton
                size="small"
                title={t('Change history')}
                onClick={() =>
                  modificationHistoryHandle(
                    PEDIGREE_CONSTANTS.auditLogType.personDetails
                  )
                }
              >
                <HistoryIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </div>
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('Possible Duplicates')}</DialogTitle>

        <DialogContent dividers>
          {renderDuplicateList(duplicateMatches)}

          <Typography variant="body1" sx={{ mt: 2 }}>
            <strong>{t('Do you want to save anyway?')}</strong>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            {t('Edit')}
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setDuplicateDialogOpen(false);
              handleSubmit();
              onDirtyChange?.(false);
            }}
          >
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
      <PreviewDialogBox
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewUrl('');
        }}
        familyId={previewResolvedFamilyId}
        previewUrl={previewUrl}
      />
    </Box>
  );
}

export default PersonMenu;
